/**
 * The TTS selection — provider, model and voice — picked as one value from one searchable list, the
 * same way the LLM picker offers provider and model together. Plus the "Andromeda default", which
 * names none of the three and leaves the backend on whatever TTS it defaults to.
 *
 * Everything here is a pure function of the catalogue fetched from the backend
 * (session/ttsCatalog.ts): the store holds the fetched catalogue and the three chosen ids, and
 * these turn them into what the picker offers, what the handshake carries, and what the readout
 * shows. Non-streaming models never reach here — the backend omits them and the catalogue
 * normaliser drops any that slip through.
 */

import type {
  CatalogModel,
  CatalogProvider,
  CatalogVoice,
  TtsCatalog,
  TtsProvider,
} from './ttsCatalog';

export type { TtsProvider } from './ttsCatalog';

/** The provider half of a TTS selection: a real one, or the backend's own default. */
export type ProviderChoice = TtsProvider | typeof DEFAULT_PROVIDER;

/** The provider value stored for "let the backend decide". */
export const DEFAULT_PROVIDER = 'andromeda-default';

/** What the gateway is told: the fields `signInRequest.metadata.tts` carries. */
export interface TtsSelection {
  provider: TtsProvider;
  voice: string;
  /** Omitted for providers with no model concept (Polly). */
  model?: string;
}

/** The three stored values the voice picker sets together. */
export interface TtsValue {
  provider: ProviderChoice;
  /** Empty for the backend default and for a provider with no models (Polly). */
  model: string;
  /** Empty for the backend default. */
  voice: string;
}

/**
 * One row of the voice picker. Either a concrete provider/model/voice triple, or the one choice that
 * names nothing — a union rather than a nullable provider, so no code path reads a voice off the
 * choice that has none.
 */
export type VoiceChoice =
  | {
      kind: 'backendDefault';
      id: typeof DEFAULT_PROVIDER;
      label: string;
    }
  | {
      kind: 'voice';
      /** Unique per row: the same voice is offered once under each of its provider's models. */
      id: string;
      provider: TtsProvider;
      providerLabel: string;
      /** Empty for a provider with no models (Polly). */
      model: string;
      modelLabel: string;
      voice: string;
      /** The voice's display name. */
      label: string;
      gender?: string;
      /** False for a provider the deployment has disabled; it is listed, and refused at sign-in. */
      available: boolean;
    };

export const BACKEND_DEFAULT_VOICE: VoiceChoice = {
  kind: 'backendDefault',
  id: DEFAULT_PROVIDER,
  label: 'Andromeda default',
};

/** The provider the catalogue lists under the given id, or null when it lists none. */
function providerIn(catalog: TtsCatalog, provider: string): CatalogProvider | null {
  return catalog.find((entry) => entry.id === provider) ?? null;
}

function choiceId(provider: string, model: string, voice: string): string {
  return `${provider}|${model}|${voice}`;
}

/**
 * Everything the voice picker offers: the backend default first, then every voice of every
 * provider, in catalogue order. A provider with models lists its voices once under each model, so
 * one row is one complete selection and picking it sets all three values at once.
 *
 * @param catalog the fetched catalogue
 * @returns the offered choices
 */
export function voiceChoices(catalog: TtsCatalog): readonly VoiceChoice[] {
  return [
    BACKEND_DEFAULT_VOICE,
    ...catalog.flatMap((entry) => {
      const models = entry.models.length > 0 ? entry.models : [{ id: '', name: '' }];
      return models.flatMap((model) =>
        entry.voices.map((voice): VoiceChoice => ({
          kind: 'voice',
          id: choiceId(entry.id, model.id, voice.id),
          provider: entry.id,
          providerLabel: entry.name,
          model: model.id,
          modelLabel: model.name,
          voice: voice.id,
          label: voice.name,
          gender: voice.gender && voice.gender !== 'neutral' ? voice.gender : undefined,
          available: entry.available,
        })),
      );
    }),
  ];
}

/**
 * The choice the stored values name: the backend default, a catalogue row, or one made up on the
 * spot for a stored voice the catalogue does not list, so the picker shows what will be sent rather
 * than silently claiming the default.
 *
 * @param catalog the fetched catalogue
 * @param value   the stored provider, model and voice
 * @returns the matching choice
 */
export function voiceChoice(catalog: TtsCatalog, value: TtsValue): VoiceChoice {
  if (value.provider === DEFAULT_PROVIDER || value.voice === '') return BACKEND_DEFAULT_VOICE;

  const id = choiceId(value.provider, value.model, value.voice);
  const known = voiceChoices(catalog).find((choice) => choice.id === id);
  if (known) return known;

  const entry = providerIn(catalog, value.provider);
  return {
    kind: 'voice',
    id,
    provider: value.provider,
    providerLabel: entry?.name ?? value.provider,
    model: value.model,
    modelLabel: modelLabel(catalog, value.provider, value.model),
    voice: value.voice,
    label: voiceLabel(catalog, value.provider, value.voice),
    available: entry?.available ?? false,
  };
}

/** The stored values a choice sets. */
export function voiceChoiceValue(choice: VoiceChoice): TtsValue {
  return choice.kind === 'backendDefault'
    ? { provider: DEFAULT_PROVIDER, model: '', voice: '' }
    : { provider: choice.provider, model: choice.model, voice: choice.voice };
}

/**
 * The heading a choice is listed under: Andromeda for the backend's own pick, otherwise the provider
 * and, where it has one, the model.
 */
export function voiceGroupLabel(choice: VoiceChoice): string {
  if (choice.kind === 'backendDefault') return 'Andromeda';
  return choice.modelLabel
    ? `${choice.providerLabel} · ${choice.modelLabel}`
    : choice.providerLabel;
}

/**
 * The choices matching a typed query, in list order. Every whitespace-separated word must appear in
 * the provider, model, voice or gender, so "rachel turbo", "polly male" and "flash" narrow the list
 * the way the words were meant.
 *
 * @param query   the typed query, possibly empty
 * @param choices the choices to search
 * @returns the matching choices; all of them for an empty query
 */
export function searchVoices(
  query: string,
  choices: readonly VoiceChoice[],
): readonly VoiceChoice[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return choices;
  return choices.filter((choice) => {
    const haystack = normalize(searchText(choice));
    return words.every((word) => haystack.includes(normalize(word)));
  });
}

function searchText(choice: VoiceChoice): string {
  return choice.kind === 'backendDefault'
    ? `andromeda ${choice.label}`
    : [
        choice.provider,
        choice.providerLabel,
        choice.model,
        choice.modelLabel,
        choice.voice,
        choice.label,
        choice.gender ?? '',
      ].join(' ');
}

/** The models the given provider offers in the catalogue; empty for the default and for Polly. */
function modelsFor(catalog: TtsCatalog, provider: ProviderChoice): readonly CatalogModel[] {
  if (provider === DEFAULT_PROVIDER) return [];
  return providerIn(catalog, provider)?.models ?? [];
}

/** The voices the given provider offers in the catalogue; empty for the default. */
function voicesFor(catalog: TtsCatalog, provider: ProviderChoice): readonly CatalogVoice[] {
  if (provider === DEFAULT_PROVIDER) return [];
  return providerIn(catalog, provider)?.voices ?? [];
}

/**
 * The provider's display name, as the catalogue named it.
 *
 * @param catalog  the fetched catalogue
 * @param provider a provider id, or the backend-default marker
 * @returns its label, or the id itself for one the catalogue does not list
 */
export function providerLabel(catalog: TtsCatalog, provider: string): string {
  if (provider === DEFAULT_PROVIDER) return 'Andromeda default';
  return providerIn(catalog, provider)?.name ?? provider;
}

/** The display label of a model id within a provider, or the id itself when unlisted. */
export function modelLabel(catalog: TtsCatalog, provider: ProviderChoice, model: string): string {
  return modelsFor(catalog, provider).find((m) => m.id === model)?.name ?? model;
}

/** The display label of a voice id within a provider, or the id itself when unlisted. */
export function voiceLabel(catalog: TtsCatalog, provider: ProviderChoice, voice: string): string {
  return voicesFor(catalog, provider).find((v) => v.id === voice)?.name ?? voice;
}

/**
 * Narrows a chosen provider/model/voice triple to what the handshake carries, or null for the
 * backend default. A model or voice that is not the chosen provider's own is dropped to that
 * provider's first, so a stale value cannot smuggle a mismatched pair into a sign-in. A provider the
 * catalogue does not list (unknown, or unavailable) resolves to null.
 *
 * @param catalog  the fetched catalogue
 * @param provider the chosen provider, possibly the backend-default marker
 * @param model    the chosen model id, possibly empty
 * @param voice    the chosen voice id, possibly empty
 * @returns the selection to send, or null to name none
 */
export function resolveTts(
  catalog: TtsCatalog,
  provider: string,
  model: string,
  voice: string,
): TtsSelection | null {
  const entry = providerIn(catalog, provider);
  if (entry === null || !entry.available) return null;

  const voiceId = entry.voices.some((v) => v.id === voice) ? voice : (entry.voices[0]?.id ?? '');
  if (!voiceId) return null;

  if (entry.models.length === 0) {
    return { provider: entry.id, voice: voiceId };
  }
  const modelId = entry.models.some((m) => m.id === model) ? model : entry.models[0].id;
  return { provider: entry.id, voice: voiceId, model: modelId };
}

/**
 * A one-line summary of a provider/model/voice triple, for the "speaks with" readout: the backend
 * default, or provider · voice (· model).
 *
 * @param catalog  the fetched catalogue
 * @param provider the chosen provider, possibly the backend-default marker
 * @param model    the chosen model id, possibly empty
 * @param voice    the chosen voice id, possibly empty
 * @returns a short label
 */
export function ttsSummary(
  catalog: TtsCatalog,
  provider: string,
  model: string,
  voice: string,
): string {
  const selection = resolveTts(catalog, provider, model, voice);
  if (selection === null) return 'Andromeda default';
  const parts = [
    providerLabel(catalog, selection.provider),
    voiceLabel(catalog, selection.provider, selection.voice),
  ];
  if (selection.model) parts.push(modelLabel(catalog, selection.provider, selection.model));
  return parts.join(' · ');
}

/** A provider value narrowed to one the catalogue lists, or the backend default for anything else. */
export function asProviderChoice(catalog: TtsCatalog, value: unknown): ProviderChoice {
  return typeof value === 'string' && providerIn(catalog, value) !== null
    ? (value as TtsProvider)
    : DEFAULT_PROVIDER;
}

/**
 * The handshake selection for the stored provider/model/voice, built from the stored values alone —
 * not gated on the catalogue being loaded. The catalogue is fetched asynchronously, so a session
 * signed in before it arrives would otherwise drop a perfectly good stored voice and fall back to the
 * backend default; the backend validates the triple and refuses an unknown one, so sending it as
 * stored is safe. Returns null only for the backend-default provider, which names nothing.
 *
 * @param provider the stored provider, possibly the backend-default marker
 * @param model    the stored model id, possibly empty
 * @param voice    the stored voice id, possibly empty
 * @returns the selection to send, or null to name none
 */
export function selectionFromStored(
  provider: string,
  model: string,
  voice: string,
): TtsSelection | null {
  if (provider === DEFAULT_PROVIDER || provider === '' || voice === '') {
    return null;
  }
  const selection: TtsSelection = { provider: provider as TtsProvider, voice };
  if (model !== '') {
    selection.model = model;
  }
  return selection;
}

/** One spelling of the separators, so "turbo v2 5" finds "eleven_turbo_v2_5" and "Turbo v2.5". */
function normalize(value: string): string {
  return value.toLowerCase().replace(/[-_.]/g, ' ');
}
